/**
 * 千里江山圖風格天空 — 絹本設色
 *
 * 宣紙暖色底 + 淡青綠漸層 + 水墨暈染雲
 * 無太陽盤面，整體柔和天光
 */

import { useMemo } from 'react'
import * as THREE from 'three/webgpu'
import {
  Fn,
  vec2,
  vec3,
  vec4,
  float,
  normalize,
  positionLocal,
  mix,
  clamp,
  smoothstep,
  pow,
  dot,
  max,
  abs,
  sin,
  cos,
  fract,
  floor,
  time as tslTime,
} from 'three/tsl'

export function ProceduralSkyQingLv() {
  const material = useMemo(() => {
    const mat = new THREE.MeshBasicNodeMaterial()
    mat.side = THREE.BackSide
    mat.depthWrite = false
    mat.fog = false

    const time = tslTime

    // ── noise ──
    const skyHash = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const h = dot(p, vec2(127.1, 311.7))
      return fract(sin(h).mul(float(43758.5453)))
    })

    const skyNoise = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const i = floor(p)
      const f = fract(p)
      const u = f.mul(f).mul(float(3).sub(f.mul(float(2))))
      const a = skyHash(i)
      const b = skyHash(i.add(vec2(1, 0)))
      const c = skyHash(i.add(vec2(0, 1)))
      const d = skyHash(i.add(vec2(1, 1)))
      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y)
    })

    const cloudFBM = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const v = float(0).toVar()
      const a = float(0.5).toVar()
      const shift = vec2(100.0, 100.0)
      v.addAssign(a.mul(skyNoise(p)))
      p.assign(p.mul(float(2.01)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(skyNoise(p)))
      p.assign(p.mul(float(2.02)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(skyNoise(p)))
      p.assign(p.mul(float(1.99)).add(shift))
      a.mulAssign(float(0.5))
      v.addAssign(a.mul(skyNoise(p)))
      return v
    })

    mat.colorNode = Fn(() => {
      const dir = normalize(positionLocal).toVar()
      const upDot = dir.y

      // ── 千里江山圖天空色調 ──
      // 天頂：淡石青（diluted azurite）
      const zenithColor = vec3(0.42, 0.58, 0.68)
      // 地平線：必須與霧色完全一致 (#d9d2c0)
      // 霧色在線性色彩空間中的值（sRGB 0.851,0.824,0.753 → linear 經 gamma 解碼）
      const horizonColor = vec3(0.694, 0.645, 0.527)
      // 地平線以下：同霧色（確保無接縫）
      const groundColor = vec3(0.694, 0.645, 0.527)

      // 高度漸層（比寫實版更低的對比度——像絹面漸層）
      const skyGrad = smoothstep(float(-0.05), float(0.55), upDot)
      const skyColor = mix(horizonColor, zenithColor, skyGrad).toVar()

      // 地平線以下
      const belowHorizon = smoothstep(float(0.0), float(-0.12), upDot)
      skyColor.assign(mix(skyColor, groundColor, belowHorizon))

      // ── 柔和光暈（代替太陽——天光暈染） ──
      const sunDir = normalize(vec3(0.4, 0.45, 0.3))
      const sunDot = max(float(0), dot(dir, sunDir))
      const glow = pow(sunDot, float(8.0)).mul(float(0.15))
      const glowColor = vec3(0.95, 0.90, 0.78) // 暖金色暈
      skyColor.assign(skyColor.add(glowColor.mul(glow)))

      // ── 水墨暈染雲（比寫實版更淡、更擴散） ──
      const cloudMask = smoothstep(float(0.08), float(0.25), upDot)
      const cloudUV = vec2(
        dir.x.div(max(abs(dir.y), float(0.001))),
        dir.z.div(max(abs(dir.y), float(0.001))),
      ).mul(float(0.12))

      const cloudP = cloudUV.add(vec2(time.mul(float(0.005)), time.mul(float(0.002))))
      const cloudDensity = cloudFBM(cloudP.mul(float(2.5)))
      // 雲更淡、更擴散（像水墨暈開）
      const cloud = smoothstep(float(0.35), float(0.7), cloudDensity)
        .mul(cloudMask)
        .mul(float(0.35)) // 比寫實版 (0.85) 更淡

      // 雲色：宣紙色偏暖
      const cloudLit = vec3(0.92, 0.90, 0.85)
      const cloudShadow = vec3(0.70, 0.68, 0.65)
      const cloudColor = mix(cloudShadow, cloudLit, smoothstep(float(0.35), float(0.55), cloudDensity))

      const finalColor = mix(skyColor, cloudColor, cloud)

      return vec4(finalColor, float(1.0))
    })()

    return mat
  }, [])

  return (
    <mesh>
      <sphereGeometry args={[1800, 32, 16]} />
      <primitive object={material} />
    </mesh>
  )
}
